import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import ImageUploader from "@/components/ImageUploader";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["Wheels", "Engine", "Exhaust", "Interior", "Suspension", "Turbos", "Brakes", "Body", "Electronics", "ECU", "Other"];
const CONDITIONS = ["New", "Used", "For Parts", "Not Working"];

export default function CreatePart() {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "",
        category: "Wheels",
        condition: "Used",
        price: "",
        location: "",
        fits: "",
        description: "",
        photos: [],
    });

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        if (!form.title || !form.price) return toast.error("Title and price are required");
        setBusy(true);
        try {
            const { data } = await api.post("/parts", {
                ...form,
                price: Number(form.price),
            });
            toast.success("Part listed");
            navigate(`/parts/${data.part_id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Layout>
            <Seo title="List a part · SRT Finder" description="Sell SRT & R/T performance parts on SRT Finder." canonicalPath="/parts/new" />
            <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="create-part-page">
                <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-onyx-500 hover:text-white mb-4">
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h1 className="text-3xl text-white mb-6"
                    style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}>
                    <Package className="w-6 h-6 text-hellcat inline-block mr-2 mb-1" />
                    List a part
                </h1>
                <form onSubmit={submit} className="space-y-4" data-testid="create-part-form">
                    <Field label="Title">
                        <input value={form.title} onChange={(e) => set("title", e.target.value)}
                               required maxLength={180}
                               className="field-input" data-testid="part-title-input"
                               placeholder="e.g., ARH Long Tube Headers - Hellcat 6.2" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category">
                            <select value={form.category} onChange={(e) => set("category", e.target.value)}
                                    className="field-input" data-testid="part-category-select">
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Condition">
                            <select value={form.condition} onChange={(e) => set("condition", e.target.value)}
                                    className="field-input" data-testid="part-condition-select">
                                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Price ($)">
                            <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)}
                                   required min={1} className="field-input" data-testid="part-price-input" placeholder="0" />
                        </Field>
                        <Field label="Location">
                            <input value={form.location} onChange={(e) => set("location", e.target.value)}
                                   className="field-input" data-testid="part-location-input" placeholder="City, State" />
                        </Field>
                    </div>
                    <Field label="Fits (which vehicles)">
                        <input value={form.fits} onChange={(e) => set("fits", e.target.value)}
                               className="field-input" data-testid="part-fits-input"
                               placeholder="e.g., Charger/Challenger 6.2L Hellcat 2015+" />
                    </Field>
                    <Field label="Description">
                        <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                                  rows={5} className="field-input" data-testid="part-description-input"
                                  placeholder="Mileage on part, install condition, reason selling…" />
                    </Field>
                    <Field label="Photos">
                        <ImageUploader
                            value={form.photos}
                            onChange={(photos) => set("photos", photos)}
                            testid="part-photos"
                            max={6}
                        />
                    </Field>
                    <div className="flex justify-end pt-2">
                        <button type="submit" disabled={busy}
                                data-testid="part-submit-btn"
                                className="btn-red px-6 py-3 text-sm font-bold">
                            {busy ? "Publishing…" : "Publish part"}
                        </button>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-caps mb-1 block">{label}</span>
            {children}
        </label>
    );
}
